import { useState, useMemo } from "react";
import { Plus, Users, Trash2, Edit2, GitMerge } from "lucide-react";
import InlinePicker from "../ui/InlinePicker";
import { COUNTRIES } from "../../lib/countries";
import Button from "../ui/Button";
import Pill from "../ui/Pill";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import AddClientForm from "./AddClientForm";
import CSVActions from "../csv/CSVActions";
import ConfirmDialog from "../ui/ConfirmDialog";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import { formatUSD } from "../../lib/formatters";
import { formatDate, toISO, today as todayDate } from "../../lib/dateUtils";
import { calculateClientLTV, calculateClientMonthsActive } from "../../lib/calculations";
import { isGHLFormat, parseGHLClients } from "../../lib/ghlImport";
import { findClientByEmail } from "../../lib/emailUtils";
import { isDateInFilter } from "../layout/GlobalDateFilter";

const FILTERS = ["All", "Active", "Free pause", "Paid pause", "Completed", "Cancelled", "Buyer", "Recurring", "One-off"];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "free_pause", label: "Free pause" },
  { value: "paid_pause", label: "Paid pause" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "buyer", label: "Buyer" },
];

const guessOfferAmount = (lineItemName) => {
  // Quick heuristic: amounts vary, but we'll default to common ranges.
  const n = (lineItemName || "").toLowerCase();
  if (n.includes("setup") || n.includes("pilot")) return 800;
  if (n.includes("trial")) return 300;
  if (n.includes("tech fee")) return 250;
  if (n.includes("done with you") || n.includes("dwy")) return 1500;
  return 1000;
};

export default function ClientList({ clients, offers, payments, settings, dateFilter, onAdd, onUpdate, onSetStatus, onCreateOffer, onBulkDelete, onMerge, onSelect, onCreateScheduledPayments }) {
  const [filter, setFilter] = useState("All");
  const [country, setCountry] = useState("");
  const [type, setType] = useState("");
  const [offerId, setOfferId] = useState("");
  const [source, setSource] = useState("");
  const [funnel, setFunnel] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [showAdd, setShowAdd] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeKeeperId, setMergeKeeperId] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [ending, setEnding] = useState(null); // { clientId, targetStatus, date }

  const toggleOne = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const countries = [...new Set(clients.map((c) => c.country).filter(Boolean))].sort();
  const types = (settings?.clientTypes?.length ? settings.clientTypes : [...new Set(clients.map((c) => c.type).filter(Boolean))]).slice().sort();
  const sources = (settings?.acquisitionSources?.length ? settings.acquisitionSources : [...new Set(clients.map((c) => c.acquisitionSource).filter(Boolean))]).slice().sort();
  const funnels = settings?.funnels?.length ? settings.funnels : [...new Set(clients.map((c) => c.funnel).filter(Boolean))];

  const filtered = useMemo(() => {
    let list = clients;
    if (filter === "Active") list = list.filter((c) => c.status === "active");
    if (filter === "Free pause") list = list.filter((c) => c.status === "free_pause" || c.status === "paused");
    if (filter === "Paid pause") list = list.filter((c) => c.status === "paid_pause");
    if (filter === "Completed") list = list.filter((c) => c.status === "completed");
    if (filter === "Cancelled") list = list.filter((c) => c.status === "cancelled" || c.status === "churned");
    if (filter === "Buyer") list = list.filter((c) => c.status === "buyer");
    if (filter === "Recurring") list = list.filter((c) => c.primaryType === "recurring");
    if (filter === "One-off") list = list.filter((c) => c.primaryType === "oneoff");
    if (country === "__none__") list = list.filter((c) => !c.country);
    else if (country) list = list.filter((c) => c.country === country);
    if (type === "__none__") list = list.filter((c) => !c.type);
    else if (type) list = list.filter((c) => c.type === type);
    if (offerId === "__none__") list = list.filter((c) => !c.primaryOfferId);
    else if (offerId) list = list.filter((c) => c.primaryOfferId === offerId);
    if (source === "__none__") list = list.filter((c) => !c.acquisitionSource);
    else if (source) list = list.filter((c) => c.acquisitionSource === source);
    if (funnel === "__none__") list = list.filter((c) => !c.funnel);
    else if (funnel) list = list.filter((c) => c.funnel === funnel);
    // Global date filter: a client is "in" the selected period strictly
    // by their startDate (the date of their first paid invoice). "All time"
    // + no month bounds = no filtering. Clients with no startDate are
    // excluded from any active date filter so 2027 doesn't surface every
    // dateless client.
    const hasDateFilter = dateFilter && (dateFilter.year !== "all" || dateFilter.fromMonth || dateFilter.toMonth);
    if (hasDateFilter) {
      list = list.filter((c) => c.startDate && isDateInFilter(c.startDate, dateFilter));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        if ((c.name || "").toLowerCase().includes(q)) return true;
        if ((c.email || "").toLowerCase().includes(q)) return true;
        if (Array.isArray(c.aliasEmails) && c.aliasEmails.some((e) => (e || "").toLowerCase().includes(q))) return true;
        return false;
      });
    }

    const decorated = list.map((c) => {
      const offer = offers.find((o) => o.id === c.primaryOfferId);
      // Price = client's monthlyRate (for recurring) or deal value (for one-off).
      // Always shown so the column never reads "—" for a client that has a price set.
      const mrr = Number(c.monthlyRate) || 0;
      const ltv = calculateClientLTV(c.id, payments);
      const months = calculateClientMonthsActive(c);
      return { ...c, mrr, ltv, months, offerName: offer?.name || "—" };
    });

    const sorted = [...decorated].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av ?? 0) - (bv ?? 0) : (bv ?? 0) - (av ?? 0);
    });
    return sorted;
  }, [clients, offers, payments, filter, country, type, offerId, source, funnel, search, dateFilter, sortKey, sortDir]);

  const sortBy = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                filter === f
                  ? "bg-brand-ink text-white border-brand-ink"
                  : "bg-white text-brand-slate border-gray-200 hover:border-gray-300 dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CSVActions
            entity="clients"
            columns={["name", "email", "country", "type", "primaryType", "primaryOfferId", "startDate", "status", "acquisitionSource", "funnel", "notes"]}
            records={clients}
            sampleRow={{ name: "Alaya Panama", email: "ops@alaya-panama.com", country: "Panama", type: "Resort", primaryType: "recurring", primaryOfferId: offers[0]?.id || "", startDate: "2025-03-01", status: "active", acquisitionSource: "Cold email", funnel: "", notes: "" }}
            beforeImport={(records) => {
              // Auto-detect GHL invoice CSV — extract unique clients by email
              if (isGHLFormat(records)) {
                return parseGHLClients(records);
              }
              return records;
            }}
            onImport={(rows) => {
              // Domain-aware dedupe: a row is skipped if its email already
              // belongs to an existing client (exact match OR same company domain).
              const liveClients = [...clients];
              const offerByName = new Map();
              offers.forEach((o) => offerByName.set(o.name.toLowerCase(), o));

              rows.forEach((r) => {
                const e = (r.email || "").toLowerCase();
                if (e && findClientByEmail(e, liveClients)) return; // already exists

                // Resolve / create primary offer from the GHL line item suggestion
                let primaryOfferId = "";
                const suggested = r._suggestedOfferName;
                if (suggested) {
                  const key = suggested.toLowerCase();
                  let offer = offerByName.get(key);
                  if (!offer && onCreateOffer) {
                    offer = onCreateOffer({
                      name: suggested,
                      type: r.primaryType,
                      defaultAmount: guessOfferAmount(suggested),
                      description: "Auto-created from GHL import",
                    });
                    offerByName.set(key, offer);
                  }
                  if (offer) primaryOfferId = offer.id;
                }
                // Strip metadata fields before saving
                const { _suggestedOfferName, ...clean } = r;
                const created = onAdd({ ...clean, primaryOfferId });
                if (created) liveClients.push(created);
              });
            }}
          />
          <Button onClick={() => setShowAdd(true)}><Plus size={14} /> Add client</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email..."
            className="pl-7 pr-3 py-1.5 border border-gray-200 dark:border-[var(--border-soft)] rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30 min-w-[240px]"
          />
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-slate pointer-events-none" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
        </div>
        <select className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]" value={country} onChange={(e) => setCountry(e.target.value)}>
          <option value="">All countries</option>
          <option value="__none__">(None)</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          <option value="__none__">(None)</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]" value={offerId} onChange={(e) => setOfferId(e.target.value)}>
          <option value="">All offers</option>
          <option value="__none__">(None)</option>
          {offers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]" value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">All sources</option>
          <option value="__none__">(None)</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {funnels.length > 0 && (
          <select className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]" value={funnel} onChange={(e) => setFunnel(e.target.value)}>
            <option value="">All funnels</option>
            <option value="__none__">(None)</option>
            {funnels.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-brand-blue-soft border border-blue-200">
          <span className="text-sm font-medium text-brand-blue-dark">{selectedIds.size} selected</span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-brand-slate">Set offer:</span>
            <select
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const newOfferId = v === "__none__" ? null : v;
                [...selectedIds].forEach((id) => onUpdate(id, { primaryOfferId: newOfferId }));
                clearSelection();
              }}
              className="px-2 py-1.5 border border-gray-200 dark:border-[var(--border-soft)] rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
              title="Apply an offer to every selected client"
            >
              <option value="">Choose offer...</option>
              <option value="__none__">(None — clear offer)</option>
              {[...offers]
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
            </select>
            <span className="text-xs text-brand-slate">Set status:</span>
            <select
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const apply = onSetStatus || ((id, status) => onUpdate(id, { status }));
                [...selectedIds].forEach((id) => apply(id, v));
                clearSelection();
              }}
              className="px-2 py-1.5 border border-gray-200 dark:border-[var(--border-soft)] rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
              title="Apply a status to every selected client"
            >
              <option value="">Choose status...</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <Button variant="secondary" size="sm" onClick={clearSelection}>Clear</Button>
            {onMerge && selectedIds.size >= 2 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const ids = [...selectedIds];
                  // Default keeper = oldest startDate among selected
                  const oldest = ids
                    .map((id) => clients.find((c) => c.id === id))
                    .filter(Boolean)
                    .sort((a, b) => (a.startDate || "9999").localeCompare(b.startDate || "9999"))[0];
                  setMergeKeeperId(oldest?.id || ids[0]);
                  setMergeOpen(true);
                }}
                title="Merge selected clients into one"
              >
                <GitMerge size={13} /> Merge {selectedIds.size}
              </Button>
            )}
            <Button variant="danger" size="sm" onClick={() => setConfirmBulk(true)}>
              <Trash2 size={13} /> Delete {selectedIds.size}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card">
        {filtered.length === 0 ? (
          <EmptyState icon={Users} title="No clients" message="Add your first client or load sample data to get started." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: "32px" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "36px" }} />
              </colgroup>
              <thead className="text-xs uppercase tracking-wide text-brand-slate border-b border-gray-100 dark:border-[var(--border-soft)]">
                <tr>
                  <th className="px-2 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id))}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds(new Set(filtered.map((c) => c.id)));
                        else clearSelection();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium cursor-pointer hover:text-brand-ink" onClick={() => sortBy("name")}>Client</th>
                  <th className="px-3 py-2 text-left font-medium">Country</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Offer</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-brand-ink" onClick={() => sortBy("mrr")}>Price</th>
                  <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-brand-ink" onClick={() => sortBy("ltv")}>Lifetime</th>
                  <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-brand-ink" onClick={() => sortBy("months")}>Mo.</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
            <tbody>
              {filtered.map((c) => (
                <TR key={c.id} onClick={() => onSelect(c.id)}>
                  <TD className="w-8" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                      className="cursor-pointer"
                    />
                  </TD>
                  <TD className="font-medium">{c.name}</TD>
                  <TD>
                    <InlinePicker
                      value={c.country || ""}
                      options={[{ value: "", label: "—" }, ...COUNTRIES.map((co) => ({ value: co, label: co }))]}
                      onChange={(v) => onUpdate?.(c.id, { country: v })}
                      placeholder="—"
                    />
                  </TD>
                  <TD>
                    <InlinePicker
                      value={c.type || ""}
                      options={[{ value: "", label: "—" }, ...types.map((t) => ({ value: t, label: t }))]}
                      onChange={(v) => onUpdate?.(c.id, { type: v })}
                      placeholder="—"
                    />
                  </TD>
                  <TD>
                    <InlinePicker
                      value={c.primaryOfferId || ""}
                      options={[{ value: "", label: "— No offer —" }, ...offers.map((o) => ({ value: o.id, label: o.name }))]}
                      onChange={(v) => {
                        if (!v) return onUpdate?.(c.id, { primaryOfferId: "" });
                        const picked = offers.find((o) => o.id === v);
                        onUpdate?.(c.id, picked ? { primaryOfferId: v, primaryType: picked.type } : { primaryOfferId: v });
                      }}
                      placeholder="— No offer —"
                      tone="muted"
                    />
                  </TD>
                  <TD>
                    {(() => {
                      const statusValue = c.status === "paused" ? "free_pause" : c.status === "churned" ? "cancelled" : c.status;
                      const statusTone = statusValue === "active" ? "green"
                        : statusValue === "cancelled" ? "red"
                        : statusValue === "completed" ? "blue"
                        : "amber";
                      return (
                        <InlinePicker
                          value={statusValue}
                          options={STATUS_OPTIONS}
                          tone={statusTone}
                          onChange={(v) => {
                            if (v === "cancelled" || v === "completed") {
                              // Open date prompt — both states need a real end date
                              setEnding({ clientId: c.id, targetStatus: v, date: c.churnDate || toISO(todayDate()) });
                              return;
                            }
                            // Other statuses: preserve existing end date (Ahmed can clear via Edit)
                            onUpdate?.(c.id, { status: v });
                          }}
                        />
                      );
                    })()}
                  </TD>
                  <TD className="text-right">{c.mrr ? formatUSD(c.mrr) : "—"}</TD>
                  <TD className="text-right">{formatUSD(c.ltv)}</TD>
                  <TD className="text-right">{c.months}</TD>
                  <TD onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setEditing(c)}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate hover:text-brand-blue"
                      title="Edit client"
                    >
                      <Edit2 size={13} />
                    </button>
                  </TD>
                </TR>
              ))}
            </tbody>
            </table>
          </div>
        )}
      </div>

      <AddClientForm
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={onAdd}
        offers={offers}
        settings={settings}
        onCreateOffer={onCreateOffer}
        onCreateScheduledPayments={onCreateScheduledPayments}
      />
      <AddClientForm
        open={!!editing}
        onClose={() => setEditing(null)}
        onSubmit={(data) => editing && onUpdate?.(editing.id, data)}
        offers={offers}
        settings={settings}
        onCreateOffer={onCreateOffer}
        onCreateScheduledPayments={onCreateScheduledPayments}
        initial={editing || undefined}
        existingPayments={editing ? payments.filter((p) => p.clientId === editing.id) : []}
      />
      <ConfirmDialog
        open={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Delete ${selectedIds.size} clients?`}
        message={`This will permanently delete ${selectedIds.size} client${selectedIds.size > 1 ? "s" : ""} and all their payments. Cannot be undone.`}
        confirmLabel={`Delete ${selectedIds.size}`}
        onConfirm={() => {
          onBulkDelete([...selectedIds]);
          clearSelection();
        }}
      />
      <Modal
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        title="Merge clients"
        size="md"
      >
        {(() => {
          const selected = [...selectedIds]
            .map((id) => clients.find((c) => c.id === id))
            .filter(Boolean);
          const keeper = selected.find((c) => c.id === mergeKeeperId) || selected[0];
          if (!keeper || selected.length < 2) return null;
          const absorbed = selected.filter((c) => c.id !== keeper.id);
          const absorbedEmails = absorbed.map((c) => c.email).filter(Boolean);
          const payCount = (cid) => payments.filter((p) => p.clientId === cid).length;
          const totalPayments = selected.reduce((sum, c) => sum + payCount(c.id), 0);
          return (
            <div className="space-y-4">
              <div>
                <div className="text-xs font-medium text-brand-slate uppercase tracking-wide mb-2">
                  Pick the client to keep
                </div>
                <div className="space-y-1.5">
                  {selected.map((c) => (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${c.id === keeper.id ? "border-brand-blue bg-brand-blue-soft" : "border-gray-200 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]"}`}
                    >
                      <input
                        type="radio"
                        name="mergeKeeper"
                        checked={c.id === keeper.id}
                        onChange={() => setMergeKeeperId(c.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-brand-ink dark:text-[var(--text-primary)]">{c.name}</div>
                        <div className="text-xs text-brand-slate truncate">
                          {c.email || "no email"}
                          {Array.isArray(c.aliasEmails) && c.aliasEmails.length > 0 && (
                            <span> · also {c.aliasEmails.join(", ")}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-brand-slate whitespace-nowrap">
                        {payCount(c.id)} payment{payCount(c.id) === 1 ? "" : "s"}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="text-xs text-brand-slate space-y-1 px-3 py-2 rounded bg-amber-50 border border-amber-200">
                <div><strong>{keeper.name}</strong> will absorb {absorbed.length} other client{absorbed.length === 1 ? "" : "s"}.</div>
                {absorbedEmails.length > 0 && (
                  <div>Aliased emails added: {absorbedEmails.join(", ")}</div>
                )}
                <div>{totalPayments} payment{totalPayments === 1 ? "" : "s"} will all be visible under {keeper.name}.</div>
                <div>The absorbed client{absorbed.length === 1 ? "" : "s"} will be deleted. This cannot be undone.</div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setMergeOpen(false)}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={() => {
                    onMerge(keeper.id, absorbed.map((c) => c.id));
                    setMergeOpen(false);
                    clearSelection();
                  }}
                >
                  <GitMerge size={13} /> Merge into {keeper.name}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal
        open={!!ending}
        onClose={() => setEnding(null)}
        title={ending?.targetStatus === "completed" ? "Mark as completed" : "Mark as cancelled"}
        size="sm"
      >
        {ending && (
          <div className="space-y-3">
            <p className="text-sm text-brand-slate">
              {ending.targetStatus === "completed"
                ? "Pick the date the project ended. Used for lifetime, not counted in churn."
                : "Pick the actual cancellation date. Counts as churn in this month."}
            </p>
            <Input
              label="End date *"
              type="date"
              value={ending.date}
              onChange={(e) => setEnding((s) => ({ ...s, date: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setEnding(null)}>Cancel</Button>
              <Button
                variant={ending.targetStatus === "completed" ? "primary" : "danger"}
                onClick={() => {
                  onUpdate?.(ending.clientId, { status: ending.targetStatus, churnDate: ending.date });
                  setEnding(null);
                }}
              >
                {ending.targetStatus === "completed" ? "Mark completed" : "Mark cancelled"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
